import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { customsDeclarations } from '@/api/customsDeclarations';
import { goodsReceipts } from '@/api/goodsReceipts';
import { contractors as contractorsApi, orgs, nomenclature as nomApi } from '@/api/catalogs';
import { partyCodesText } from '@/shared/utils/partyCodes';
import SaveXlsxButton from '@/shared/components/SaveXlsxButton';

const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtQty = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 0, maximumFractionDigits: 4 });

const MONTHS_UK = [
  'січня', 'лютого', 'березня', 'квітня', 'травня', 'червня',
  'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня',
];

function formatDateUk(iso: string): string {
  const d = new Date(iso);
  return `« ${d.getDate()} » ${MONTHS_UK[d.getMonth()]!} ${d.getFullYear()} р.`;
}
function formatShortDate(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

export default function CustomsDeclarationPrintPage() {
  const { id } = useParams();
  const docQ = useQuery({
    queryKey: ['customs-declaration', id ?? ''],
    queryFn: () => customsDeclarations.get(id!),
    enabled: !!id,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const ctrQ = useQuery({ queryKey: ['contractors-all'], queryFn: () => contractorsApi.listAll() });
  const nomQ = useQuery({ queryKey: ['nomenclature-full'], queryFn: () => nomApi.listAll() });

  // Подгружаем строки поступлений по уникальным ids
  const receiptItemIds = useMemo(
    () => Array.from(new Set(docQ.data?.items.map((it) => it.goods_receipt_item_id) ?? [])),
    [docQ.data],
  );

  // Получаем уникальные receipts, к которым относятся строки.
  // Для печати нужны: nomenclature_id, quantity, amount_without_vat.
  // Простейший подход — для каждого item.goods_receipt_item_id выяснить
  // его поступление через cache. Так как у нас нет API «get receipt item
  // by id», подгружаем все receipts организации и ищем там.
  const receiptsQ = useQuery({
    queryKey: ['goods-receipts-for-print', docQ.data?.organization_id ?? ''],
    queryFn: () => goodsReceipts.list({
      organization_id: docQ.data!.organization_id, posted: true, limit: 500,
    }),
    enabled: !!docQ.data,
  });

  // Параллельно подтягиваем сами receipts с items
  const receiptIdsToLoad = useMemo(() => {
    if (!receiptsQ.data) return [];
    // Для простоты: грузим все receipts из списка (их немного), берём из них строки
    return receiptsQ.data.items.map((r) => r.id);
  }, [receiptsQ.data]);

  const fullReceipts = useQuery({
    queryKey: ['goods-receipts-full', receiptIdsToLoad.join(',')],
    queryFn: () => Promise.all(receiptIdsToLoad.map((rid) => goodsReceipts.get(rid))),
    enabled: receiptIdsToLoad.length > 0 && receiptItemIds.length > 0,
  });

  const riMap = useMemo(() => {
    const m: Record<string, { nom: string; qty: string; amount: string }> = {};
    fullReceipts.data?.forEach((rcpt) => {
      rcpt.items.forEach((it) => {
        if (it.id) m[it.id] = {
          nom: it.nomenclature_id, qty: it.quantity, amount: it.amount_without_vat,
        };
      });
    });
    return m;
  }, [fullReceipts.data]);

  const nomName = useMemo(() => {
    const m = new Map<string, string>();
    nomQ.data?.items.forEach((n) => m.set(n.id, n.name));
    return m;
  }, [nomQ.data]);

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  if (!docQ.data) {
    return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  }
  const doc = docQ.data;
  const org = orgsQ.data?.items.find((o) => o.id === doc.organization_id);

  return (
    <div className="print-page" style={{
      padding: '20mm', fontFamily: 'Times, serif', fontSize: '11pt', lineHeight: 1.4,
      maxWidth: '210mm', margin: '0 auto', background: 'white',
    }}>
      <div className="print-hide" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button onClick={() => window.print()} className="primary">друкувати</button>
        <button onClick={() => window.history.back()}>назад</button>
        <SaveXlsxButton />
      </div>

      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <div style={{ fontSize: '10pt' }}>{org?.full_name ?? org?.name ?? ''}</div>
        {partyCodesText(org) && (
          <div style={{ fontSize: '10pt', color: '#555' }}>{partyCodesText(org)}</div>
        )}
      </div>

      <h2 style={{ textAlign: 'center', fontSize: '13pt', margin: '1rem 0 0.3rem' }}>
        Бухгалтерська довідка-розрахунок
      </h2>
      <h3 style={{ textAlign: 'center', fontSize: '11pt', margin: '0 0 1rem', fontWeight: 'normal' }}>
        до митної декларації № {doc.customs_number} від {formatShortDate(doc.customs_date)}
      </h3>

      <table style={{ width: '100%', marginBottom: '0.8rem', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', padding: '0.2rem 0.5rem' }}>
              Внутрішній номер: <strong>{doc.document_number}</strong>
            </td>
            <td style={{ width: '50%', padding: '0.2rem 0.5rem' }}>
              Дата документа: {formatDateUk(doc.document_date)}
            </td>
          </tr>
          {(() => {
            const supId = (doc as { supplier_contractor_id?: string | null })
              .supplier_contractor_id;
            const sup = supId
              ? ctrQ.data?.items.find((c) => c.id === supId) : undefined;
            if (!supId) return null;
            return (
              <tr>
                <td style={{ padding: '0.2rem 0.5rem' }} colSpan={2}>
                  Постачальник: <strong>{sup?.name ?? '—'}</strong>
                  {partyCodesText(sup) ? ` (${partyCodesText(sup)})` : ''}
                </td>
              </tr>
            );
          })()}
          {doc.country_origin_code && (
            <tr>
              <td style={{ padding: '0.2rem 0.5rem' }} colSpan={2}>
                Країна походження: <strong>{doc.country_origin_code}</strong>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10pt' }}>
        <thead>
          <tr style={{ borderTop: '2px solid #000', borderBottom: '1px solid #000' }}>
            <th style={{ padding: '0.3rem', textAlign: 'center', width: '4%' }}>№</th>
            <th style={{ padding: '0.3rem', textAlign: 'left' }}>Товар</th>
            <th style={{ padding: '0.3rem', textAlign: 'right', width: '8%' }}>К-сть</th>
            <th style={{ padding: '0.3rem', textAlign: 'right', width: '12%' }}>Митна вартість</th>
            <th style={{ padding: '0.3rem', textAlign: 'right', width: '9%' }}>Мито</th>
            <th style={{ padding: '0.3rem', textAlign: 'right', width: '9%' }}>Акциз</th>
            <th style={{ padding: '0.3rem', textAlign: 'right', width: '9%' }}>Збір</th>
            <th style={{ padding: '0.3rem', textAlign: 'right', width: '9%' }}>ПДВ</th>
            <th style={{ padding: '0.3rem', textAlign: 'right', width: '11%' }}>Разом платежів</th>
            <th style={{ padding: '0.3rem', textAlign: 'right', width: '11%' }}>Загалом</th>
          </tr>
        </thead>
        <tbody>
          {doc.items.map((it) => {
            const ri = riMap[it.goods_receipt_item_id];
            const name = ri ? nomName.get(ri.nom) ?? '—' : '(рядок надходження)';
            return (
              <tr key={it.id} style={{ borderBottom: '1px solid #ccc' }}>
                <td style={{ padding: '0.3rem', textAlign: 'center' }}>{it.line_number}</td>
                <td style={{ padding: '0.3rem' }}>{name}</td>
                <td style={{ padding: '0.3rem', textAlign: 'right' }}>
                  {ri ? fmtQty(ri.qty) : '—'}
                </td>
                <td style={{ padding: '0.3rem', textAlign: 'right' }}>{fmt(it.customs_value)}</td>
                <td style={{ padding: '0.3rem', textAlign: 'right' }}>{fmt(it.duty_amount)}</td>
                <td style={{ padding: '0.3rem', textAlign: 'right' }}>{fmt(it.excise_amount)}</td>
                <td style={{ padding: '0.3rem', textAlign: 'right' }}>{fmt(it.fee_allocated ?? '0')}</td>
                <td style={{ padding: '0.3rem', textAlign: 'right' }}>{fmt(it.vat_amount)}</td>
                <td style={{ padding: '0.3rem', textAlign: 'right' }}>
                  {fmt(Number(it.duty_amount) + Number(it.excise_amount) + Number(it.fee_allocated ?? 0) + Number(it.vat_amount))}
                </td>
                <td style={{ padding: '0.3rem', textAlign: 'right', fontWeight: 600 }}>
                  {fmt(Number(it.customs_value) + Number(it.duty_amount) + Number(it.excise_amount)
                    + Number(it.fee_allocated ?? 0) + Number(it.vat_amount))}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #000', fontWeight: 700 }}>
            <td colSpan={3} style={{ padding: '0.3rem', textAlign: 'right' }}>Усього:</td>
            <td style={{ padding: '0.3rem', textAlign: 'right' }}>{fmt(doc.total_customs_value)}</td>
            <td style={{ padding: '0.3rem', textAlign: 'right' }}>{fmt(doc.total_duty)}</td>
            <td style={{ padding: '0.3rem', textAlign: 'right' }}>{fmt(doc.total_excise)}</td>
            <td style={{ padding: '0.3rem', textAlign: 'right' }}>{fmt(doc.customs_fee)}</td>
            <td style={{ padding: '0.3rem', textAlign: 'right' }}>{fmt(doc.total_vat)}</td>
            <td style={{ padding: '0.3rem', textAlign: 'right' }}>
              {fmt(Number(doc.total_duty) + Number(doc.total_excise) + Number(doc.customs_fee) + Number(doc.total_vat))}
            </td>
            <td style={{ padding: '0.3rem', textAlign: 'right' }}>
              {fmt(Number(doc.total_customs_value) + Number(doc.total_duty) + Number(doc.total_excise)
                + Number(doc.customs_fee) + Number(doc.total_vat))}
            </td>
          </tr>
        </tfoot>
      </table>

      <div style={{ marginTop: '1.5rem', padding: '0.75rem', background: '#f8fafc', fontSize: '10pt' }}>
        <strong>Бухгалтерські проводки:</strong>
        <div style={{ marginTop: '0.3rem' }}>
          • Дт 281 — Кт 6851 на {fmt(doc.total_cost_addition)} грн
          (мито + акциз + збір за оформлення, у собівартість товарів)
        </div>
        {Number(doc.total_vat) > 0 && (
          <div>
            • Дт 6442 — Кт 6851 на {fmt(doc.total_vat)} грн (ПДВ при імпорті,
            податковий кредит на підставі ВМД)
          </div>
        )}
      </div>

      <table style={{ width: '100%', marginTop: '2.5rem', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', padding: '0.5rem' }}>
              <div>Керівник:</div>
              <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center' }}>
                ___________________
              </div>
              <div style={{ textAlign: 'center', fontSize: '9pt' }}>(підпис, П.І.Б.)</div>
            </td>
            <td style={{ width: '50%', padding: '0.5rem' }}>
              <div>Головний бухгалтер:</div>
              <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center' }}>
                ___________________
              </div>
              <div style={{ textAlign: 'center', fontSize: '9pt' }}>(підпис, П.І.Б.)</div>
            </td>
          </tr>
        </tbody>
      </table>

    </div>
  );
}
